import { Component, NgZone, OnDestroy, OnInit } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRoute } from "@angular/router";
import { LoggingService, PromptConfirmDialogComponent, PromptConfirmInput, PromptConfirmMode, WindowService } from "@merri/common";
import { Subject } from "rxjs";
import { take, takeUntil } from "rxjs/operators";
import { COLLAB_ERROR_TEXT } from "src/app/core";
import { EventContextService } from "src/app/features/events";
import { VisionboardVersion } from "../../models";
import { VisionboardVersionsService, VisionboardWorkspaceItemsStoreService } from "../../services";

@Component({
    selector: "app-visionboard-workspace-controls",
    templateUrl: "./visionboard-workspace-controls.component.html",
    styleUrls: ["./visionboard-workspace-controls.component.scss"],
    standalone: false
})
export class VisionboardWorkspaceControlsComponent implements OnInit, OnDestroy {
  private destroyed$: Subject<void> = new Subject<void>();

  selectedVersion: VisionboardVersion;
  saveText: string;
  failedToSave: boolean;
  mobileView: boolean;
  isGuestUser: boolean;

  constructor(
    private visionboardVersionsService: VisionboardVersionsService,
    private visionboardWorkspaceItemsStoreService: VisionboardWorkspaceItemsStoreService,
    private eventContextService: EventContextService,
    private matDialog: MatDialog,
    private route: ActivatedRoute,
    private ngZone: NgZone,
    private _loggingService: LoggingService,
    private windowService: WindowService,
  ) {}

  ngOnInit(): void {
    this.isGuestUser = this.eventContextService.isGuestEvent();
    this.visionboardVersionsService.selectedVersion$.pipe(takeUntil(this.destroyed$)).subscribe(version => {
      this.setSelectedVersion(version);
      this.setSaveText("", false);
    });

    this.visionboardWorkspaceItemsStoreService.saving$.pipe(takeUntil(this.destroyed$)).subscribe(saving => {
      if (saving && saving.version === this.selectedVersion) {
        if (saving.inProgress) {
          this.setSaveText("Saving...", false);
        } else {
          if (saving.success) {
            this.setSaveText("Saved", false);
            setTimeout(() => {
              this.setSaveText("", false);
            }, 1000);
          } else if (saving.staleVersion) {
            this.handleStaleVersion();
          } else {
            this.setSaveText("Failed to save", true);
          }
        }
      }
    });

    this.windowService
      .observeMax$(1080)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: o => {
          this.mobileView = o.matches;
        },
      });
  }

  private handleStaleVersion(): void {
    this.ngZone.run(() => {
      this.matDialog.closeAll();

      const dialog = this.matDialog.open(PromptConfirmDialogComponent, {
        autoFocus: false,
        disableClose: true,
        data: {
          message: COLLAB_ERROR_TEXT,
          mode: PromptConfirmMode.Ok,
        } as PromptConfirmInput,
      });

      dialog
        .afterClosed()
        .pipe(take(1))
        .subscribe(() => {
          LoggingService.log("UserEventPageComponent - stale version dialog closed");

          this._loggingService.logRemoteInfo("VB stale version kickout", "VisionboardWorkspaceControlsComponent.handleStaleVersion");

          this.eventContextService.navigateToVenue(this.route);
        });
    });
  }

  private setSaveText(text: string, failedToSave: boolean): void {
    this.ngZone.run(() => {
      this.saveText = text;
      this.failedToSave = failedToSave;
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  onVersionSelected(version: VisionboardVersion): void {
    this.visionboardVersionsService.setSelectedVersion(version);
  }

  private setSelectedVersion(version: VisionboardVersion): void {
    this.selectedVersion = version;
  }
}
